(function () {
  'use strict';

  const CONFIG = {
    refreshInterval: 300000,
    minViewing: 1,
    maxViewing: 10,
    minCart: 1,
    maxCart: 5,
    showViewingAbove: 1,
    showCartAbove: 1,

    // Stock display
    lowStockThreshold: 5,   // below this = "Low stock: Only X left"
    stockDisplayCap: 10,    // at/above this = "10+ in stock"

    // When stock is low, scale down viewing/cart caps proportionally
    // e.g. stock of 2 with threshold 5 -> caps multiplied by 2/5
    lowStockCapFloorViewing: 6,  // never go below this even when scaling
    lowStockCapFloorCart: 2,
  };

  const SS_KEY = 'socialProofCounts';
  const MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

  // ---- Storage helpers (localStorage - shared across tabs) ----
  function loadStoredCounts() {
    try {
      const raw = localStorage.getItem(SS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function saveStoredCounts(counts) {
    try {
      localStorage.setItem(SS_KEY, JSON.stringify(counts));
    } catch (e) {
      // localStorage unavailable (private mode etc) - fail silently
    }
  }

  const countsByKey = loadStoredCounts();

  // ---- Utility ----
  function rand(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  function nudge(current, min, max) {
    const delta = Math.floor(Math.random() * 5) - 2;
    return Math.min(max, Math.max(min, current + delta));
  }

  // Derive effective caps based on current stock level
  function getEffectiveCaps(stockQty) {
    if (stockQty === null || stockQty >= CONFIG.lowStockThreshold) {
      return { maxViewing: CONFIG.maxViewing, maxCart: CONFIG.maxCart };
    }
    // Scale down proportionally, but never below the floor
    const ratio = Math.max(stockQty, 0) / CONFIG.lowStockThreshold;
    const scaledViewing = Math.max(
      CONFIG.lowStockCapFloorViewing,
      Math.floor(CONFIG.maxViewing * ratio)
    );
    const scaledCart = Math.max(
      CONFIG.lowStockCapFloorCart,
      Math.floor(CONFIG.maxCart * ratio)
    );
    return {
      maxViewing: Math.min(CONFIG.maxViewing, scaledViewing),
      maxCart: Math.min(CONFIG.maxCart, scaledCart),
    };
  }

  function generateCounts(stockQty) {
    const caps = getEffectiveCaps(stockQty);
    const viewing = rand(CONFIG.minViewing, Math.max(CONFIG.minViewing, caps.maxViewing));
    const cartCap = Math.min(caps.maxCart, Math.floor(viewing * 0.7));
    const inCart = rand(
      Math.min(CONFIG.minCart, Math.max(cartCap, 0)),
      Math.max(cartCap, 0)
    );
    return { viewing: viewing, inCart: inCart };
  }

  function nudgeCounts(current, stockQty) {
    const caps = getEffectiveCaps(stockQty);
    const viewing = nudge(current.viewing, CONFIG.minViewing, Math.max(CONFIG.minViewing, caps.maxViewing));
    const cartCap = Math.min(caps.maxCart, Math.floor(viewing * 0.7));
    const inCart = nudge(
      current.inCart,
      Math.min(CONFIG.minCart, Math.max(cartCap, 0)),
      Math.max(cartCap, 0)
    );
    return { viewing: viewing, inCart: inCart };
  }

  // ---- Inventory helpers ----
  function getInventoryData(el) {
    const script = el.querySelector('[data-variant-inventory]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return {};
    }
  }

  function getVariantStock(inventoryData, variantId) {
    const entry = inventoryData[String(variantId)];
    if (!entry) return { available: true, quantity: null };

    // If inventory isn't tracked, treat as unlimited/in-stock
    if (!entry.inventory_management) {
      return { available: true, quantity: null };
    }

    const qty = entry.inventory_quantity;
    const available = entry.available;

    return { available: available, quantity: qty };
  }

  // ---- Rendering ----
  function renderBadge(el, counts, stock) {
    const context = el.dataset.context;
    const stockEl = el.querySelector('.social-proof-badge__stock');
    const viewingEl = el.querySelector('.social-proof-badge__viewing');
    const cartEl = el.querySelector('.social-proof-badge__inner');

    if (!viewingEl || !cartEl || !stockEl) return;

    // --- Stock badge ---
    if (stock.quantity === null) {
      // Untracked inventory - hide stock badge
      stockEl.style.display = 'none';
    } else {
      const stockText = stockEl.querySelector('.social-proof-badge__text');
      const qty = stock.quantity;

      if (qty <= 0) {
        stockText.textContent = `Out of stock`;
        stockEl.dataset.lowStock = 'false';
        stockEl.dataset.outOfStock = 'true';
        stockEl.style.display = 'inline-flex';
      } else if (qty < CONFIG.lowStockThreshold) {
        stockText.textContent = `Low stock: Only ${qty} left`;
        stockEl.dataset.lowStock = 'true';
        stockEl.dataset.outOfStock = 'false';
        stockEl.style.display = 'inline-flex';
      } else if (qty >= CONFIG.stockDisplayCap) {
        stockText.textContent = `${CONFIG.stockDisplayCap}+ in stock`;
        stockEl.dataset.lowStock = 'false';
        stockEl.dataset.outOfStock = 'false';
        stockEl.style.display = 'inline-flex';
      } else {
        stockText.textContent = `${qty} in stock`;
        stockEl.dataset.lowStock = 'false';
        stockEl.dataset.outOfStock = 'false';
        stockEl.style.display = 'inline-flex';
      }
    }

    // --- Viewing now badge ---
    const showViewing = counts.viewing > CONFIG.showViewingAbove;
    const viewingText = viewingEl.querySelector('.social-proof-badge__text');
    if (showViewing) {
      viewingText.textContent = `${counts.viewing} viewing now`;
      viewingEl.style.display = 'inline-flex';
    } else {
      viewingEl.style.display = 'none';
    }

    // --- In carts badge ---
    // Hidden entirely if variant is out of stock
    const showCart = counts.inCart > CONFIG.showCartAbove && stock.available !== false;
    const cartText = cartEl.querySelector('.social-proof-badge__text');
    if (showCart) {
      const suffix = context === 'cart-item' ? ' right now' : '';
      cartText.textContent = `In ${counts.inCart} people's carts${suffix}`;
      cartEl.style.display = 'inline-flex';
    } else {
      cartEl.style.display = 'none';
    }
  }

  // ---- Init / refresh ----
  function processEl(el) {
    const productId = el.dataset.productId;
    const variantId = el.dataset.currentVariantId;
    const key = `${productId}-${variantId}`;

    // Re-read latest shared state from localStorage in case another tab updated it
    const latest = loadStoredCounts();
    Object.assign(countsByKey, latest);

    const inventoryData = getInventoryData(el);
    const stock = getVariantStock(inventoryData, variantId);

    const stored = countsByKey[key];
    const isStale = !stored || (Date.now() - stored.timestamp > MAX_AGE_MS);

    if (isStale) {
      const fresh = generateCounts(stock.quantity);
      countsByKey[key] = { ...fresh, timestamp: Date.now() };
    }

    renderBadge(el, countsByKey[key], stock);
  }

  function initAll() {
    document.querySelectorAll('[data-social-proof]').forEach(processEl);
    saveStoredCounts(countsByKey);
  }

  // Called when Canopy refreshes the PDP block on variant change
  function onVariantChange(e) {
    document.querySelectorAll('[data-social-proof]').forEach(function (el) {
      // Update the current variant id if Canopy passes it via event detail
      if (e && e.detail && e.detail.variant && e.detail.variant.id) {
        el.dataset.currentVariantId = e.detail.variant.id;
      }
      processEl(el);
    });
    saveStoredCounts(countsByKey);
  }

  // Periodic refresh - nudges counts for currently-rendered badges only
  setInterval(function () {
    document.querySelectorAll('[data-social-proof]').forEach(function (el) {
      const productId = el.dataset.productId;
      const variantId = el.dataset.currentVariantId;
      const key = `${productId}-${variantId}`;

      const inventoryData = getInventoryData(el);
      const stock = getVariantStock(inventoryData, variantId);

      if (countsByKey[key]) {
        const nudged = nudgeCounts(countsByKey[key], stock.quantity);
        countsByKey[key] = { ...nudged, timestamp: Date.now() };
      }
      renderBadge(el, countsByKey[key], stock);
    });
    saveStoredCounts(countsByKey);
  }, CONFIG.refreshInterval);

  // ---- Event wiring ----
  // Canopy variant change event (Clean Canvas theme)
  document.addEventListener('on:variant:change', onVariantChange);

  // Fallback for Theme Editor / section re-renders
  document.addEventListener('shopify:section:load', onVariantChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

})();